import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { ApiError } from "@tenergy/web-core/api";
import { WalletFailure, type WalletSessionValue } from "@tenergy/web-core/wallet";
import { adminRequest, type ApiTarget } from "../api/request.js";
import type { AdminIdentity } from "../api/types.js";
import { hasBrowserKey } from "../auth/browser-key.js";
import type { LedgerAccount, LedgerConnection } from "../auth/ledger.js";
import { clearAdminSession, onAdminSessionChange, readAdminSession, rememberAdminAddress } from "../auth/session-store.js";
import { signInToNileFromMainnet, signInWithPasskey, signInWithWallet, signOutOf } from "../auth/sign-in-flow.js";

/**
 * Signing into the admin panel (docs/product/admin-auth.md §2–§4).
 *
 * ITS OWN SIGN-IN, NOT THE DASHBOARD'S. The admin routes, the admin table of people, the admin session bound to this
 * browser's key; a dashboard session of the same wallet opens nothing here, and this one opens nothing there. Three
 * ways in, all equal: a TRON wallet (TronLink, WalletConnect), a Ledger (Ethereum app — the safest, since the device
 * shows the text — or the TRON app), or a passkey bound to the address.
 *
 * One hook per API: mainnet for the panel, Nile for the Nile page, each with its own session and key. The Nile one
 * opens itself from the mainnet session when there is one (signInToNileFromMainnet).
 */

export type AdminPhase =
  | { kind: "restoring" }
  | { kind: "anonymous" }
  | { kind: "connecting" }
  /** The Ledger is connected and its first accounts read; the admin picks the one that signs in. */
  | { kind: "choose-account"; accounts: LedgerAccount[]; reading: boolean; error: string | null }
  | { kind: "signing" }
  | { kind: "signed-in" }
  | { kind: "failed"; reason: SignInFailure; message: string | null };

export type SignInFailure =
  | "locked"
  | "wrong_network"
  | "rejected"
  | "refused"
  | "unavailable"
  | "closed"
  | "device"
  | "offline";

export type SignInWay =
  | { kind: "tron-wallet" }
  /** Connects and lists the first Ledger Live accounts with their addresses (owner 2026-10-31: no address to type). */
  | { kind: "ledger"; app: "ethereum" | "tron"; transport: "usb" | "ble" }
  | { kind: "passkey"; address: string };

export interface AdminSessionValue {
  phase: AdminPhase;
  identity: AdminIdentity | null;
  /** True once the stored session (if any) has been checked with the API. */
  ready: boolean;
  signIn(way: SignInWay): Promise<void>;
  /** In `choose-account`: sign in with this Ledger account. */
  chooseAccount(account: LedgerAccount): Promise<void>;
  /** In `choose-account`: read the next accounts onto the list. */
  moreAccounts(): Promise<void>;
  /** In `choose-account`: read the account at a typed derivation path onto the top of the list. */
  accountAtPath(path: string): Promise<void>;
  /** Leave `choose-account` without signing. */
  cancel(): void;
  signOut(): Promise<void>;
}

/** Accounts listed after connecting, and per "Show 5 more": each is one read, a few hundred ms over Bluetooth. */
const LISTED_ACCOUNTS = 5;

/** The open account picker: the connected device, which app, what is listed, the next Ledger Live index to read. */
interface Picking {
  link: LedgerConnection;
  app: "ethereum" | "tron";
  accounts: LedgerAccount[];
  next: number;
}

function classify(error: unknown): { reason: SignInFailure; message: string | null } {
  if (error instanceof WalletFailure) {
    switch (error.code) {
      case "locked":
        return { reason: "locked", message: null };
      case "wrong_network":
        return { reason: "wrong_network", message: null };
      case "rejected":
        return { reason: "rejected", message: null };
      case "connection_closed":
        return { reason: "closed", message: null };
      default:
        return { reason: "unavailable", message: null };
    }
  }
  if (error instanceof ApiError) {
    // One answer for every refusal (wrong signature, no access, frozen): the API tells nobody which it was.
    if (error.slug === "admin_session_invalid") return { reason: "refused", message: null };
    return { reason: "offline", message: error.message };
  }
  if (error instanceof DOMException && (error.name === "NotAllowedError" || error.name === "AbortError")) {
    return { reason: "rejected", message: null };
  }
  if (error instanceof Error && error.name === "LedgerFailure") return { reason: "device", message: error.message };
  if (error !== null && typeof error === "object" && "_tag" in error) {
    return { reason: "device", message: ledgerModule?.describeLedgerError(error) ?? null };
  }
  return { reason: "offline", message: error instanceof Error ? error.message : null };
}

let ledgerModule: typeof import("../auth/ledger.js") | null = null;
const loadLedger = async () => (ledgerModule ??= await import("../auth/ledger.js"));

/** The Ledger module, loaded on first use (the confirmation dialog takes it from here too). */
export { loadLedger };

export function useAdminSession(wallet: WalletSessionValue, target: ApiTarget = "mainnet"): AdminSessionValue {
  const [identity, setIdentity] = useState<AdminIdentity | null>(null);
  const [phase, setPhase] = useState<AdminPhase>({ kind: "restoring" });
  /** The connected Ledger while the admin picks an account. */
  const picking = useRef<Picking | null>(null);

  const readIdentity = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      const me = await adminRequest<AdminIdentity>("/admin/session", { target, ...(signal ? { signal } : {}) });
      setIdentity({ address: me.address, role: me.role, method: me.method });
      // A passkey is asked for by address (non-discoverable keys); the last signed-in one is remembered for it.
      rememberAdminAddress(me.address);
    },
    [target],
  );

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    void (async () => {
      try {
        if (readAdminSession(target) === null || !(await hasBrowserKey(target))) {
          clearAdminSession(target);
          // The Nile page under a mainnet session opens Nile from it; only when that is refused is the Nile sign-in shown.
          const fromMainnet =
            target === "nile" && readAdminSession("mainnet") !== null && (await signInToNileFromMainnet().then(() => true, () => false));
          if (cancelled) return;
          if (!fromMainnet) {
            setPhase({ kind: "anonymous" });
            return;
          }
        }
        await readIdentity(controller.signal);
        if (!cancelled) setPhase({ kind: "signed-in" });
      } catch (error) {
        if (cancelled) return;
        // An ended session is simply "signed out"; a network failure is shown as one, never as "no access".
        if (error instanceof ApiError && error.status === 401) setPhase({ kind: "anonymous" });
        else setPhase({ kind: "failed", ...classify(error) });
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [readIdentity, target]);

  // A session the API ended mid-use (idle, frozen, ended by root) drops the panel back to the sign-in screen.
  useEffect(
    () =>
      onAdminSessionChange((slot) => {
        if (slot !== target || readAdminSession(target) !== null) return;
        setIdentity(null);
        setPhase((current) => (current.kind === "signed-in" ? { kind: "anonymous" } : current));
      }),
    [target],
  );

  const signIn = useCallback(
    async (way: SignInWay): Promise<void> => {
      try {
        setPhase({ kind: "connecting" });
        if (way.kind === "passkey") {
          setPhase({ kind: "signing" });
          await signInWithPasskey(target, way.address.trim());
        } else if (way.kind === "ledger") {
          const ledger = await loadLedger();
          const { link, accounts } = await ledger.withLedger(way.transport, async (link) => ({
            link,
            accounts: await ledger.ledgerAccounts(link, way.app, 0, LISTED_ACCOUNTS),
          }));
          picking.current = { link, app: way.app, accounts, next: LISTED_ACCOUNTS };
          setPhase({ kind: "choose-account", accounts, reading: false, error: null });
          return;
        } else {
          const address = wallet.address ?? (await wallet.requestConnection());
          setPhase({ kind: "signing" });
          await signInWithWallet(target, address, wallet.signMessage);
        }
        await readIdentity();
        setPhase({ kind: "signed-in" });
      } catch (error) {
        setPhase({ kind: "failed", ...classify(error) });
      }
    },
    [wallet, readIdentity, target],
  );

  const chooseAccount = useCallback(
    async (account: LedgerAccount): Promise<void> => {
      const pick = picking.current;
      if (pick === null) return;
      try {
        setPhase({ kind: "signing" });
        const ledger = await loadLedger();
        await signInWithWallet(target, account.address, (text) => ledger.ledgerSignMessage(pick.link, account, text));
        picking.current = null;
        await readIdentity();
        setPhase({ kind: "signed-in" });
      } catch (error) {
        setPhase({ kind: "failed", ...classify(error) });
      }
    },
    [readIdentity, target],
  );

  /** Reads onto the open picker; a device error is shown in the picker, which stays open. */
  const readOntoPicker = useCallback(async (read: (pick: Picking) => Promise<LedgerAccount[]>): Promise<void> => {
    const pick = picking.current;
    if (pick === null) return;
    setPhase({ kind: "choose-account", accounts: pick.accounts, reading: true, error: null });
    try {
      pick.accounts = await read(pick);
      setPhase({ kind: "choose-account", accounts: pick.accounts, reading: false, error: null });
    } catch (error) {
      setPhase({ kind: "choose-account", accounts: pick.accounts, reading: false, error: classify(error).message ?? "The Ledger did not answer." });
    }
  }, []);

  const moreAccounts = useCallback(
    () =>
      readOntoPicker(async (pick) => {
        const ledger = await loadLedger();
        const next = await ledger.ledgerAccounts(pick.link, pick.app, pick.next, LISTED_ACCOUNTS);
        pick.next += LISTED_ACCOUNTS;
        return [...pick.accounts, ...next];
      }),
    [readOntoPicker],
  );

  const accountAtPath = useCallback(
    (path: string) =>
      readOntoPicker(async (pick) => {
        const ledger = await loadLedger();
        const account = await ledger.ledgerAccountAt(pick.link, pick.app, path);
        return [account, ...pick.accounts.filter((a) => a.path !== account.path)];
      }),
    [readOntoPicker],
  );

  const cancel = useCallback(() => {
    picking.current = null;
    setPhase({ kind: "anonymous" });
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    await signOutOf(target);
    if (target === "mainnet") await wallet.disconnect().catch(() => undefined);
    setIdentity(null);
    setPhase({ kind: "anonymous" });
  }, [wallet, target]);

  return {
    phase,
    identity,
    ready: phase.kind !== "restoring",
    signIn,
    chooseAccount,
    moreAccounts,
    accountAtPath,
    cancel,
    signOut,
  };
}
